import React, { useState, useMemo } from "react";
import { Table, Button, Tooltip, Space, Badge } from "antd";
import { EditOutlined, LockOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import User from "../../types/User";
import EntityInfo from "../../types/EntityInfo";
import { useTexts } from "../../hooks/useTexts";
import { usePresence } from "../../context/PresenceContext";
import { formatDateToFullString } from "../../util/date";
import TablePagination from "../TablePagination/TablePagination";

type Props = {
    users: User[],
    canEdit: boolean,
    onEdit: (user: User) => void,
    onResetPassword: (user: User) => void
}

const getCityString = (cities: EntityInfo[]) => {
    if (!(cities?.length > 0)) return "-";
    return cities.map(c => c.name).join(", ");
};

const formatCities = (cities: EntityInfo[], endIndex: number) => {
    if (!(cities?.length > 0)) return "-";
    return cities.slice(0, endIndex).map(c => c.name).join(", ");
};

const formatLastSeen = (lastSeenAt: string, t: (category: string, key: string) => string): string => {
    const minutes = Math.floor((Date.now() - new Date(lastSeenAt).getTime()) / 60_000);
    if (minutes < 1) return `1 ${t('presence', 'minutesShort')}`;
    if (minutes < 60) return `${minutes} ${t('presence', 'minutesShort')}`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ${t('presence', 'hoursShort')}`;

    return formatDateToFullString(new Date(lastSeenAt));
};

const UserTable = ({ users, canEdit, onEdit, onResetPassword }: Props): JSX.Element => {
    const { t } = useTexts();
    const { isOnline } = usePresence();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return users.slice(startIndex, startIndex + pageSize);
    }, [users, currentPage, pageSize]);

    const renderStatus = (user: User) => {
        if (isOnline(user.id)) {
            return <Badge status="success" text={t('presence', 'online')} />;
        }
        if (!user.lastSeenAt) {
            return <Badge status="default" text="-" />;
        }
        return (
            <Tooltip title={formatDateToFullString(new Date(user.lastSeenAt))}>
                <Badge status="default" text={formatLastSeen(user.lastSeenAt, t)} />
            </Tooltip>
        );
    };

    const columns: ColumnsType<User> = [
        {
            title: '#',
            key: 'index',
            render: (text, record, index) => index + 1,
            width: 60,
        },
        {
            title: t('presence', 'status'),
            key: 'presence',
            width: 140,
            render: (_, record) => renderStatus(record)
        },
        {
            title: t('user', 'username'),
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: t('user', 'firstname'),
            dataIndex: 'firstname',
            key: 'firstname',
        },
        {
            title: t('user', 'lastname'),
            dataIndex: 'lastname',
            key: 'lastname',
        },
        {
            title: t('user', 'roles'),
            key: 'roles',
            render: (_, record) => record.roles
                ?.map(el => el.name)?.join(", ")
                || "-"
        },
        {
            title: t('user', 'cities'),
            key: 'cities',
            render: (_, record) => {
                if (record.cities?.length > 3) {
                    return (
                        <Tooltip title={getCityString(record.cities)}>
                            {formatCities(record.cities, 3)} ...
                        </Tooltip>
                    );
                }
                return getCityString(record.cities);
            }
        },
        {
            title: t('user', 'products'),
            key: 'products',
            render: (_, record) => {
                if (record.products?.length > 3) {
                    return (
                        <Tooltip title={getCityString(record.products)}>
                            {formatCities(record.products, 3)} ...
                        </Tooltip>
                    );
                }
                return getCityString(record.products);
            }
        }
    ];

    if (canEdit) {
        columns.push({
            title: '',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => onEdit(record)}
                    />
                    <Button
                        type="default"
                        icon={<LockOutlined />}
                        onClick={() => onResetPassword(record)}
                        title="Şifrəni yenilə"
                    />
                </Space>
            )
        });
    }

    return (
        <div style={{ marginTop: '24px' }}>
            <Table
                columns={columns}
                dataSource={paginatedUsers}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={false}
            />
            {users.length > 0 && (
                <TablePagination
                    current={currentPage}
                    pageSize={pageSize}
                    pageCount={Math.ceil(users.length / pageSize)}
                    total={users.length}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    }}
                />
            )}
        </div>
    );
};

export default UserTable;
